import { toast } from 'sonner';
import { t } from 'i18next';
import type { OAuthProvider } from './completeOAuthRedirect';

const codeOf = (error: unknown): string => {
  const e = error as { code?: string; message?: string } | null;
  return `${e?.code ?? ''} ${e?.message ?? ''}`.toLowerCase();
};

/** The user closed the Google/Apple window or cancelled — not an error to show. */
export const isOAuthCancel = (error: unknown): boolean =>
  /popup-closed-by-user|cancelled-popup-request|user-cancel|canceled|cancelled|1001|12501/.test(codeOf(error));

/** One place that turns a failed social sign-in into a short, localized message. */
export function reportOAuthError(provider: OAuthProvider, error: unknown): void {
  if (isOAuthCancel(error)) return;
  const code = codeOf(error);
  const name = { google: 'Google', apple: 'Apple', microsoft: 'Microsoft', facebook: 'Facebook' }[provider];
  if (/operation-not-allowed/.test(code)) {
    toast.info(
      t('authFlow.providerUnavailable', {
        provider: name,
        defaultValue: 'Вход через {{provider}} скоро будет доступен. Пока войдите через Google или по email.',
      }),
    );
    return;
  }
  if (/popup-blocked/.test(code)) {
    toast.error(t('authFlow.popupBlocked', 'Браузер заблокировал окно входа. Разрешите всплывающие окна и попробуйте снова.'));
    return;
  }
  if (/network/.test(code)) {
    toast.error(t('authFlow.errors.network', 'Нет связи с сервером. Проверьте интернет и попробуйте ещё раз.'));
    return;
  }
  toast.error(
    provider === 'apple'
      ? t('auth.appleError', 'Не удалось войти через Apple. Попробуйте снова.')
      : provider === 'google'
        ? t('auth.googleError', 'Не удалось войти через Google. Попробуйте снова.')
        : t('authFlow.providerError', { provider: name, defaultValue: 'Не удалось войти через {{provider}}. Попробуйте снова.' }),
  );
}
